// src/pages/VerificationPage.js
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  IconButton,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Chip,
  Stack,
  Table,
  TableBody,
  TableRow,
  TableCell,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Search as SearchIcon,
  CameraAlt as CameraIcon,
  CheckCircle as VerifiedIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  CloudUpload as UploadIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import Webcam from 'react-webcam';
import {
  searchPersonnelForVerification,
  completeVerification,
  recordNonVerification,
} from '../services/verificationService';
import PreClearanceDialog from '../components/PreClearanceDialog';
import { useAuth } from '../context/AuthContext';

const VerificationPage = () => {
  const navigate = useNavigate();
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);

  const { isAdmin } = useAuth();

  // Enumerate available cameras on mount
  React.useEffect(() => {
    navigator.mediaDevices.enumerateDevices()
      .then((devices) => {
        const videoDevices = devices.filter((d) => d.kind === 'videoinput');
        setCameras(videoDevices);
        // Default to the last camera in the list — typically the external one
        // if one is plugged in; otherwise falls back to the built-in.
        if (videoDevices.length > 0) {
          setSelectedCamera(videoDevices[videoDevices.length - 1].deviceId);
        }
      })
      .catch((err) => console.error('Camera enumeration error:', err));
  }, []);

  // Search state
  const [searchQuery, setSearchQuery]   = useState('');
  const [searching, setSearching]       = useState(false);
  const [searchError, setSearchError]   = useState('');

  // Personnel state
  const [personnel, setPersonnel] = useState(null);

  // Pre-clearance state
  const [preClearanceDialogOpen, setPreClearanceDialogOpen] = useState(false);

  // Photo state
  const [photo, setPhoto]             = useState(null);
  const [showWebcam, setShowWebcam]   = useState(false);
  const [photoMethod, setPhotoMethod] = useState('webcam'); // 'webcam' | 'upload' (admin only)
  const [uploadError, setUploadError] = useState('');

  // Camera device state
  const [cameras, setCameras]           = useState([]);
  const [selectedCamera, setSelectedCamera] = useState('');

  // Contact state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError]   = useState('');
  const [email, setEmail]             = useState('');
  const [emailError, setEmailError]   = useState('');

  // ── Multiple complaints state ─────────────────────────────────────────────
  const [hasComplaint, setHasComplaint] = useState(false);
  const [complaints, setComplaints]     = useState([{ type: '', details: '' }]);

  // Non-verification state
  const [showNonVerification, setShowNonVerification] = useState(false);
  const [nonVerifReason, setNonVerifReason]           = useState('');
  const [nonVerifDetails, setNonVerifDetails]         = useState('');
  const [nonVerifError, setNonVerifError]             = useState('');
  const [nonVerifLoading, setNonVerifLoading]         = useState(false);
  const [nonVerifSuccess, setNonVerifSuccess]         = useState(false);

  // Verification result state
  const [verificationResult, setVerificationResult] = useState(null);
  const [verifying, setVerifying]                   = useState(false);
  const [verificationError, setVerificationError]   = useState('');
  const [pendingResult, setPendingResult]           = useState(null); // flagged capture held for admin

  // Complaint types
  const complaintTypes = [
    { value: 'UNDERPAYMENT',              label: 'Underpayment'                        },
    { value: 'OVERPAYMENT',               label: 'Overpayment'                         },
    { value: 'NON_PAYMENT',               label: 'Non-Payment'                         },
    { value: 'WRONG_ALLOWANCE',           label: 'Wrong Allowance'                     },
    { value: 'WRONG_DEDUCTION',           label: 'Wrong Deduction'                     },
    { value: 'DATE_OF_BIRTH_ERROR',       label: 'Date of Birth Error'                 },
    { value: 'SERVICE_NUMBER',            label: 'Service Number'                      },
    { value: 'PENSION_PIN_AND_NUMBER',    label: 'Pension Pin and Number'              },
    { value: 'NHF_NUMBER',               label: 'NHF Number'                          },
    { value: 'DATE_OF_RETIREMENT',        label: 'Date of Retirement (DOR)'            },
    { value: 'DATE_OF_FIRST_APPOINTMENT', label: 'Date of First Appointment (DOFA)'    },
    { value: 'GRADE_STEP',               label: 'Grade/Step'                          },
    { value: 'RANK',                      label: 'Rank'                                },
    { value: 'GENDER',                    label: 'Gender'                              },
    { value: 'OTHER',                     label: 'Other'                               },
  ];

  // ── Validation helpers ────────────────────────────────────────────────────

  const handlePhoneChange = (value) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 11);
    setPhoneNumber(cleaned);
    if (cleaned.length > 0 && cleaned.length < 11) {
      setPhoneError('Phone number must be exactly 11 digits');
    } else if (cleaned.length === 11 && !cleaned.startsWith('0')) {
      setPhoneError('Phone number must start with 0');
    } else {
      setPhoneError('');
    }
  };

  const handleEmailChange = (value) => {
    setEmail(value);
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (value.length > 0 && !emailPattern.test(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  // ── Multiple complaints helpers ───────────────────────────────────────────

  const handleAddComplaint = () => {
    setComplaints((prev) => [...prev, { type: '', details: '' }]);
  };

  const handleRemoveComplaint = (index) => {
    setComplaints((prev) => prev.filter((_, i) => i !== index));
  };

  const handleComplaintChange = (index, field, value) => {
    setComplaints((prev) =>
      prev.map((c, i) => (i === index ? { ...c, [field]: value } : c))
    );
  };

  // ── Search ────────────────────────────────────────────────────────────────
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchError('Please enter Service Number, IPPIS Number, or Account Number');
      return;
    }
    setSearching(true);
    setSearchError('');
    setPersonnel(null);
    setVerificationResult(null);
    setPendingResult(null);
    setPhoto(null);
    setPhoneNumber(''); setPhoneError('');
    setEmail(''); setEmailError('');
    setHasComplaint(false);
    setComplaints([{ type: '', details: '' }]);
    setPhotoMethod('webcam');
    setUploadError('');

    try {
      const response = await searchPersonnelForVerification(searchQuery.trim());
      setPersonnel(response.data.personnel);
    } catch (error) {
      setSearchError(error.response?.data?.message || 'Personnel not found');
    } finally {
      setSearching(false);
    }
  };

  // ── Pre-clearance ─────────────────────────────────────────────────────────

  const handlePreClearanceSuccess = async () => {
    setPreClearanceDialogOpen(false);
    try {
      const response = await searchPersonnelForVerification(searchQuery.trim());
      setPersonnel(response.data.personnel);
    } catch (error) {
      console.error('Failed to refresh personnel data:', error);
    }
  };

  // ── Photo: webcam ─────────────────────────────────────────────────────────

  const capturePhoto = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    setPhoto(imageSrc);
    setShowWebcam(false);
  };

  // ── Photo: file upload (admin only) ──────────────────────────────────────

  const handlePhotoUpload = (e) => {
    setUploadError('');
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      setUploadError('Only JPEG and PNG images are accepted');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image must be smaller than 5 MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => setPhoto(ev.target.result);
    reader.readAsDataURL(file);
  };

  // ── Photo method toggle (admin only) ─────────────────────────────────────

  const handlePhotoMethodChange = (_, newMethod) => {
    if (!newMethod) return;
    setPhotoMethod(newMethod);
    setPhoto(null);
    setShowWebcam(false);
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Complete verification ─────────────────────────────────────────────────

  const handleCompleteVerification = async () => {
    setVerificationError('');

    if (!personnel) {
      setVerificationError('No personnel selected');
      return;
    }

    if (!personnel.preClearance && !isAdmin) {
      setVerificationError('Personnel must be pre-cleared before verification');
      return;
    }

    if (!photo) {
      setVerificationError('Photo is required. Please capture or upload personnel photo');
      return;
    }

    if (phoneNumber && phoneNumber.length !== 11) {
      setPhoneError('Phone number must be exactly 11 digits');
      setVerificationError('Please fix phone number error');
      return;
    }
    if (phoneNumber && !phoneNumber.startsWith('0')) {
      setPhoneError('Phone number must start with 0');
      setVerificationError('Please fix phone number error');
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email && !emailPattern.test(email)) {
      setEmailError('Please enter a valid email address');
      setVerificationError('Please fix email error');
      return;
    }

    if (!phoneNumber && !email) {
      setVerificationError('At least phone number or email is required');
      return;
    }

    // Validate complaints: every complaint must have both type and details
    if (hasComplaint) {
      const incomplete = complaints.some((c) => !c.type || !c.details.trim());
      if (incomplete) {
        setVerificationError('Please complete all complaint entries — each needs a type and details');
        return;
      }
      if (complaints.length === 0) {
        setVerificationError('Please add at least one complaint or uncheck the complaint option');
        return;
      }
    }

    setVerifying(true);

    try {
      const verificationData = {
        personnelId:         personnel.id,
        photoBase64:         photo,
        phoneNumber:         phoneNumber || null,
        email:               email || null,
        adminOverrideReason: isAdmin && !personnel.preClearance ? 'ADMIN_OVERRIDE' : null,
        complaints:          hasComplaint ? complaints : [],
      };

      const response = await completeVerification(verificationData);

      // Flagged service number: capture is held for admin approval (no slip yet)
      if (response.status === 'PENDING_APPROVAL') {
        setPendingResult({
          name:          `${personnel.lastName} ${personnel.firstName}`,
          serviceNumber: personnel.serviceNumber,
          submittedAt:   response.data?.submittedAt,
          message:       response.message,
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      setVerificationResult({
        personnel:       response.data.personnel,
        verification:    response.data.verification,
        referenceNumber: response.data.referenceNumber,
      });

      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Verification error:', error);
      setVerificationError(error.response?.data?.message || 'Failed to complete verification');
    } finally {
      setVerifying(false);
    }
  };

  // ── Non-verification ─────────────────────────────────────────────────────

  const handleNonVerification = async () => {
    setNonVerifError('');
    if (!nonVerifReason) {
      setNonVerifError('Please select a reason');
      return;
    }
    setNonVerifLoading(true);
    try {
      await recordNonVerification({
        personnelId: personnel.id,
        reason:      nonVerifReason,
        details:     nonVerifDetails || null,
      });
      setNonVerifSuccess(true);
    } catch (err) {
      setNonVerifError(err.response?.data?.message || 'Failed to record non-verification');
    } finally {
      setNonVerifLoading(false);
    }
  };

  // ── Reset ─────────────────────────────────────────────────────────────────

  const handleReset = () => {
    setSearchQuery('');
    setPersonnel(null);
    setPhoto(null);
    setPhoneNumber(''); setPhoneError('');
    setEmail(''); setEmailError('');
    setHasComplaint(false);
    setComplaints([{ type: '', details: '' }]);
    setVerificationResult(null);
    setPendingResult(null);
    setSearchError(''); setVerificationError('');
    setShowWebcam(false);
    setPhotoMethod('webcam');
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    // Re-enumerate cameras and reset to last (external) camera
    navigator.mediaDevices.enumerateDevices()
      .then((devices) => {
        const videoDevices = devices.filter((d) => d.kind === 'videoinput');
        setCameras(videoDevices);
        if (videoDevices.length > 0) {
          setSelectedCamera(videoDevices[videoDevices.length - 1].deviceId);
        }
      })
      .catch(() => {});
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  // ============================================
  // RENDER: VERIFICATION RESULT
  // ============================================
  if (verificationResult) {
    const { personnel: p, verification, referenceNumber } = verificationResult;
    const apiBaseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace('/api', '');
    const pdfUrl = verification.pdfUrl ? `${apiBaseUrl}${verification.pdfUrl}` : null;

    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box display="flex" alignItems="center" gap={2.5}>
            <IconButton onClick={() => navigate('/')}><BackIcon /></IconButton>
            <Box
              component="img"
              src={require('../assets/images/immgrationlogo1.png')}
              alt="Nigeria Immigration Service"
              sx={{ height: 50, width: 'auto', objectFit: 'contain' }}
            />
            <Box>
              <Typography variant="h6" fontWeight="600"
                sx={{ color: '#00a651', lineHeight: 1.2, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '1.1rem' }}>
                Verification Complete
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                NIS Emolument Verification Exercise
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Alert severity="success" sx={{ mb: 3 }} icon={<VerifiedIcon />}>
          <Typography variant="h6" fontWeight="600" gutterBottom>✅ Verification Successful!</Typography>
          <Typography variant="body1">
            Reference Number: <strong>{referenceNumber || verification.referenceNumber || 'N/A'}</strong>
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Verified on: {new Date(verification.verifiedAt).toLocaleString('en-GB')}
          </Typography>
        </Alert>

        <Paper elevation={3} sx={{ p: 4 }}>
          <Grid container spacing={4}>
            {/* Photo */}
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                {photo && (
                  <img src={photo} alt="Personnel"
                    style={{ width: '100%', maxWidth: 250, height: 'auto',
                      border: '3px solid #1976d2', borderRadius: 8 }} />
                )}
                <Chip icon={<VerifiedIcon />} label="VERIFIED" color="success"
                  sx={{ mt: 2, fontWeight: 600 }} />
              </Box>
            </Grid>

            {/* Details */}
            <Grid item xs={12} md={9}>
              <Typography variant="h5" gutterBottom fontWeight="600" sx={{ mb: 3 }}>
                VERIFICATION RECORD
              </Typography>

              <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 2, color: 'primary.main', textTransform: 'uppercase' }}>
                Personal Information
              </Typography>
              <Table size="small" sx={{ mb: 3, border: '1px solid', borderColor: 'grey.300' }}>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ width: '25%', fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Service Number</TableCell>
                    <TableCell sx={{ width: '25%', border: '1px solid', borderColor: 'grey.300' }}>{p.serviceNumber}</TableCell>
                    <TableCell sx={{ width: '25%', fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>IPPIS Number</TableCell>
                    <TableCell sx={{ width: '25%', border: '1px solid', borderColor: 'grey.300' }}>{p.ippisNumber}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Full Name</TableCell>
                    <TableCell colSpan={3} sx={{ border: '1px solid', borderColor: 'grey.300' }}>{p.firstName} {p.middleName} {p.lastName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Gender</TableCell>
                    <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>{p.gender}</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Date of Birth</TableCell>
                    <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>{formatDate(p.birthDate)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 2, color: 'primary.main', textTransform: 'uppercase' }}>
                Employment Information
              </Typography>
              <Table size="small" sx={{ mb: 3, border: '1px solid', borderColor: 'grey.300' }}>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ width: '25%', fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Rank</TableCell>
                    <TableCell sx={{ width: '25%', border: '1px solid', borderColor: 'grey.300' }}>{p.rankName}</TableCell>
                    <TableCell sx={{ width: '25%', fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Grade</TableCell>
                    <TableCell sx={{ width: '25%', border: '1px solid', borderColor: 'grey.300' }}>{p.gradeName} {p.gradeLevel}/{p.gradeStep}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Date of First Appointment</TableCell>
                    <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>{formatDate(p.dateOfFirstAppointment)}</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Expected Retirement</TableCell>
                    <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>{formatDate(p.expectedRetirementDate)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Command</TableCell>
                    <TableCell colSpan={3} sx={{ border: '1px solid', borderColor: 'grey.300' }}>{p.command.code} - {p.command.name}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Zone</TableCell>
                    <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>{p.command.zone}</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>State</TableCell>
                    <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>{p.command.state}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 2, color: 'primary.main', textTransform: 'uppercase' }}>
                Banking Information
              </Typography>
              <Table size="small" sx={{ mb: 3, border: '1px solid', borderColor: 'grey.300' }}>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ width: '25%', fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Bank Name</TableCell>
                    <TableCell sx={{ width: '25%', border: '1px solid', borderColor: 'grey.300' }}>{p.bankName}</TableCell>
                    <TableCell sx={{ width: '25%', fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Account Number</TableCell>
                    <TableCell sx={{ width: '25%', border: '1px solid', borderColor: 'grey.300' }}>{p.accountNumber}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>PFA Name</TableCell>
                    <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>{p.pfaName}</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>PENCOM ID</TableCell>
                    <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>{p.pencomId}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>NHF Number</TableCell>
                    <TableCell colSpan={3} sx={{ border: '1px solid', borderColor: 'grey.300' }}>{p.nhfNumber || 'N/A'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 2, color: 'primary.main', textTransform: 'uppercase' }}>
                Contact Information
              </Typography>
              <Table size="small" sx={{ border: '1px solid', borderColor: 'grey.300' }}>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ width: '25%', fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Phone Number</TableCell>
                    <TableCell sx={{ width: '25%', border: '1px solid', borderColor: 'grey.300' }}>{p.phoneNumber || 'N/A'}</TableCell>
                    <TableCell sx={{ width: '25%', fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Email Address</TableCell>
                    <TableCell sx={{ width: '25%', border: '1px solid', borderColor: 'grey.300' }}>{p.email || 'N/A'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            {pdfUrl ? (
              <>
                <Button variant="contained" size="large" startIcon={<DownloadIcon />}
                  onClick={() => window.open(pdfUrl, '_blank')}>
                  Download PDF
                </Button>
                <Button variant="outlined" size="large" startIcon={<PrintIcon />}
                  onClick={() => {
                    const w = window.open(pdfUrl, '_blank');
                    if (w) { w.onload = () => w.print(); }
                  }}>
                  Print PDF
                </Button>
              </>
            ) : (
              <Alert severity="warning">PDF is being generated. Please refresh in a moment.</Alert>
            )}
            <Button variant="outlined" size="large" onClick={handleReset}>
              Verify Another Personnel
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  // ============================================
  // RENDER: FLAGGED CAPTURE HELD FOR ADMIN APPROVAL
  // ============================================
  if (pendingResult) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={2} sx={{ p: 4 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight="600" gutterBottom>Capture submitted for approval</Typography>
            <Typography variant="body1">{pendingResult.serviceNumber} — {pendingResult.name}</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {pendingResult.message || 'This capture is awaiting admin approval. No slip is issued until it is approved.'}
            </Typography>
            {pendingResult.submittedAt && (
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Submitted: {new Date(pendingResult.submittedAt).toLocaleString('en-GB')}
              </Typography>
            )}
          </Alert>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" onClick={handleReset}>Capture Another Personnel</Button>
            <Button variant="outlined" onClick={() => navigate('/')}>Back to Dashboard</Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  // ============================================
  // RENDER: VERIFICATION FORM
  // ============================================
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2.5}>
          <IconButton onClick={() => navigate('/')}><BackIcon /></IconButton>
          <Box
            component="img"
            src={require('../assets/images/immgrationlogo1.png')}
            alt="Nigeria Immigration Service"
            sx={{ height: 50, width: 'auto', objectFit: 'contain' }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight="600"
              sx={{ color: '#00a651', lineHeight: 1.2, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '1.1rem' }}>
              Personnel Verification
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              NIS Emolument Verification Exercise
            </Typography>
          </Box>
          {isAdmin && (
            <Chip label="Admin Mode" color="error" size="small" sx={{ fontWeight: 'bold' }} />
          )}
        </Box>
      </Paper>

      {/* Search Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Search Personnel</Typography>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            label="Service Number, IPPIS Number, or Account Number"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="e.g., 56334 or NI9001234 or 0123456789"
            disabled={searching}
          />
          <Button variant="contained" size="large"
            startIcon={searching ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
            onClick={handleSearch} disabled={searching} sx={{ minWidth: 120 }}>
            {searching ? 'Searching...' : 'Search'}
          </Button>
        </Box>
        {searchError && <Alert severity="error" sx={{ mt: 2 }}>{searchError}</Alert>}
      </Paper>

      {/* Already Verified Alert */}
      {personnel && personnel.verification && (
        <Alert severity="info" sx={{ mb: 3 }}
          action={
            <Button size="small" variant="outlined"
              onClick={() => setVerificationResult({
                personnel,
                verification: personnel.verification,
                referenceNumber: personnel.verification.referenceNumber,
              })}>
              View Record
            </Button>
          }>
          <Typography variant="body1" fontWeight="600">ℹ️ Personnel Already Verified</Typography>
          <Typography variant="body2">
            Verified on: {new Date(personnel.verification.verifiedAt).toLocaleString('en-GB')}
            <br />Reference: <strong>{personnel.verification.referenceNumber}</strong>
          </Typography>
        </Alert>
      )}

      {/* Flagged record notice (only for flagged service numbers) */}
      {personnel && !personnel.verification && personnel.captureFlag && (
        <Alert sx={{ mb: 3 }}
          severity={personnel.captureFlag.blockedForYou || personnel.captureFlag.pending ? 'error' : 'warning'}>
          <Typography variant="body1" fontWeight="600">
            {personnel.captureFlag.blockedForYou
              ? 'You cannot capture this personnel'
              : personnel.captureFlag.pending
                ? 'Capture awaiting admin approval'
                : 'This capture needs admin approval'}
          </Typography>
          <Typography variant="body2">
            {personnel.captureFlag.blockedForYou
              ? 'The previous capture of this record was made from your account. Another verification officer must capture this personnel.'
              : personnel.captureFlag.pending
                ? `Captured by ${personnel.captureFlag.pending.submittedByName || 'an officer'} on ${new Date(personnel.captureFlag.pending.submittedAt).toLocaleString('en-GB')}. The admin will approve or reject it.`
                : 'Capture as normal. The capture will be held for admin approval and the slip is issued once it is approved.'}
          </Typography>
          {isAdmin && personnel.captureFlag.finding && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Reason flagged: {personnel.captureFlag.finding}. Reversed capture: {personnel.captureFlag.originalReference} by {personnel.captureFlag.originalVerifiedByName}.
            </Typography>
          )}
          {isAdmin && personnel.captureFlag.pendingId && (
            <Button size="small" variant="outlined" sx={{ mt: 1 }}
              onClick={() => navigate(`/flagged-recaptures?open=${personnel.captureFlag.pendingId}`)}>
              Review capture
            </Button>
          )}
        </Alert>
      )}

      {/* Verification Form */}
      {personnel && !personnel.verification &&
        !(personnel.captureFlag && (personnel.captureFlag.blockedForYou || personnel.captureFlag.pending)) && (
        <Paper sx={{ p: 4 }}>

          {/* ── Pre-clearance / Admin Override Section ── */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {isAdmin ? 'Verification Authority' : 'Pre-clearance Status'}
            </Typography>

            {isAdmin && personnel.preClearance && (
              <Alert severity="success">
                ✅ Pre-cleared by: {personnel.preClearance.clearingOfficer.firstName}{' '}
                {personnel.preClearance.clearingOfficer.lastName}
                <br />
                <Typography variant="caption">
                  Notes: {personnel.preClearance.notes || 'No additional notes'}
                </Typography>
              </Alert>
            )}

            {isAdmin && !personnel.preClearance && (
              <Alert severity="info">
                ℹ️ This personnel has no pre-clearance. As admin you can proceed with verification directly.
              </Alert>
            )}

            {!isAdmin && !personnel.preClearance && (
              <>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  ⚠️ This personnel must be pre-cleared by a Comptroller or Principal before verification.
                </Alert>
                <Button variant="contained" color="warning"
                  onClick={() => setPreClearanceDialogOpen(true)}>
                  Record Pre-clearance
                </Button>
              </>
            )}

            {!isAdmin && personnel.preClearance && (
              <Alert severity="success">
                ✅ Pre-cleared by: {personnel.preClearance.clearingOfficer.firstName}{' '}
                {personnel.preClearance.clearingOfficer.lastName}
                <br />
                <Typography variant="caption">
                  Notes: {personnel.preClearance.notes || 'No additional notes'}
                </Typography>
              </Alert>
            )}
          </Box>

          {(personnel.preClearance || isAdmin) && (
            <>
              <Divider sx={{ mb: 4 }} />

              <Grid container spacing={4}>
                {/* LEFT: Photo */}
                <Grid item xs={12} md={4}>
                  <Typography variant="h6" gutterBottom>Capture Photo</Typography>

                  {isAdmin && (
                    <Box sx={{ mb: 2 }}>
                      <ToggleButtonGroup
                        value={photoMethod}
                        exclusive
                        onChange={handlePhotoMethodChange}
                        fullWidth
                        size="small"
                      >
                        <ToggleButton value="webcam">
                          <CameraIcon sx={{ mr: 1, fontSize: 18 }} /> Webcam
                        </ToggleButton>
                        <ToggleButton value="upload">
                          <UploadIcon sx={{ mr: 1, fontSize: 18 }} /> Upload Photo
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  )}

                  <Box sx={{
                    border: '2px dashed',
                    borderColor: photo ? 'success.main' : 'grey.400',
                    borderRadius: 2, p: 3, textAlign: 'center',
                    minHeight: 400, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    bgcolor: photo ? 'success.lighter' : 'grey.50',
                  }}>

                    {photoMethod === 'webcam' && !photo && !showWebcam && (
                      <>
                        <CameraIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          No photo captured
                        </Typography>
                        <Button variant="contained" startIcon={<CameraIcon />}
                          onClick={() => setShowWebcam(true)} sx={{ mt: 2 }}>
                          Open Camera
                        </Button>
                      </>
                    )}

                    {photoMethod === 'webcam' && showWebcam && (
                      <Box sx={{ width: '100%' }}>
                        {cameras.length > 1 && (
                          <TextField
                            select fullWidth size="small"
                            label="Select Camera"
                            value={selectedCamera}
                            onChange={(e) => setSelectedCamera(e.target.value)}
                            sx={{ mb: 1 }}
                          >
                            {cameras.map((cam, i) => (
                              <MenuItem key={cam.deviceId} value={cam.deviceId}>
                                {cam.label || `Camera ${i + 1}`}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Webcam audio={false} ref={webcamRef}
                            screenshotFormat="image/jpeg"
                            width={280}
                            height={320}
                            videoConstraints={{
                              width: 280, height: 320,
                              deviceId: selectedCamera ? { exact: selectedCamera } : undefined,
                            }}
                            style={{ borderRadius: 8, display: 'block' }} />
                        </Box>
                        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} justifyContent="center">
                          <Button variant="contained" onClick={capturePhoto}>Capture</Button>
                          <Button variant="outlined" onClick={() => setShowWebcam(false)}>Cancel</Button>
                        </Stack>
                      </Box>
                    )}

                    {photoMethod === 'upload' && !photo && (
                      <>
                        <UploadIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Upload a photo from your device
                        </Typography>
                        <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 2 }}>
                          JPEG or PNG, max 5 MB
                        </Typography>
                        {uploadError && (
                          <Alert severity="error" sx={{ mb: 2, width: '100%' }}>{uploadError}</Alert>
                        )}
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/jpg,image/png"
                          style={{ display: 'none' }}
                          onChange={handlePhotoUpload}
                        />
                        <Button variant="contained" startIcon={<UploadIcon />}
                          onClick={() => fileInputRef.current?.click()} sx={{ mt: 1 }}>
                          Browse Files
                        </Button>
                      </>
                    )}

                    {photo && !showWebcam && (
                      <>
                        <img src={photo} alt="Captured"
                          style={{ width: '100%', maxWidth: '300px', height: 'auto',
                            borderRadius: 8, border: '3px solid #4caf50' }} />
                        <Chip icon={<VerifiedIcon />}
                          label={photoMethod === 'upload' ? 'Photo Uploaded' : 'Photo Captured'}
                          color="success" sx={{ mt: 2, fontWeight: 600 }} />
                        <Button variant="outlined" size="small"
                          onClick={() => {
                            setPhoto(null);
                            if (photoMethod === 'webcam') setShowWebcam(true);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                          sx={{ mt: 1 }}>
                          {photoMethod === 'upload' ? 'Change Photo' : 'Retake Photo'}
                        </Button>
                      </>
                    )}
                  </Box>
                </Grid>

                {/* RIGHT: Personnel Details */}
                <Grid item xs={12} md={8}>
                  <Typography variant="h6" gutterBottom>Personnel Information</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 3 }}>
                    Please review these details with the personnel
                  </Typography>

                  <Typography variant="subtitle2" fontWeight="700" sx={{ mb: 1, color: 'primary.main', textTransform: 'uppercase' }}>
                    Personal Information
                  </Typography>
                  <Table size="small" sx={{ mb: 3, border: '1px solid', borderColor: 'grey.300' }}>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ width: '25%', fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Service Number</TableCell>
                        <TableCell sx={{ width: '25%', border: '1px solid', borderColor: 'grey.300' }}>{personnel.serviceNumber}</TableCell>
                        <TableCell sx={{ width: '25%', fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>IPPIS Number</TableCell>
                        <TableCell sx={{ width: '25%', border: '1px solid', borderColor: 'grey.300' }}>{personnel.ippisNumber}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Full Name</TableCell>
                        <TableCell colSpan={3} sx={{ border: '1px solid', borderColor: 'grey.300' }}>
                          {personnel.firstName} {personnel.middleName} {personnel.lastName}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Gender</TableCell>
                        <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>{personnel.gender}</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Date of Birth</TableCell>
                        <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>{formatDate(personnel.birthDate)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>

                  <Typography variant="subtitle2" fontWeight="700" sx={{ mb: 1, color: 'primary.main', textTransform: 'uppercase' }}>
                    Employment Information
                  </Typography>
                  <Table size="small" sx={{ mb: 3, border: '1px solid', borderColor: 'grey.300' }}>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ width: '25%', fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Rank</TableCell>
                        <TableCell sx={{ width: '25%', border: '1px solid', borderColor: 'grey.300' }}>{personnel.rankName}</TableCell>
                        <TableCell sx={{ width: '25%', fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Grade</TableCell>
                        <TableCell sx={{ width: '25%', border: '1px solid', borderColor: 'grey.300' }}>{personnel.gradeName} {personnel.gradeLevel}/{personnel.gradeStep}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Date of First Appointment</TableCell>
                        <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>{formatDate(personnel.dateOfFirstAppointment)}</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Expected Retirement</TableCell>
                        <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>{formatDate(personnel.expectedRetirementDate)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Command</TableCell>
                        <TableCell colSpan={3} sx={{ border: '1px solid', borderColor: 'grey.300' }}>
                          {personnel.command.code} - {personnel.command.name}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Zone</TableCell>
                        <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>{personnel.command.zone}</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>State</TableCell>
                        <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>{personnel.command.state}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>

                  <Typography variant="subtitle2" fontWeight="700" sx={{ mb: 1, color: 'primary.main', textTransform: 'uppercase' }}>
                    Banking Information
                  </Typography>
                  <Table size="small" sx={{ border: '1px solid', borderColor: 'grey.300' }}>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ width: '25%', fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Bank Name</TableCell>
                        <TableCell sx={{ width: '25%', border: '1px solid', borderColor: 'grey.300' }}>{personnel.bankName}</TableCell>
                        <TableCell sx={{ width: '25%', fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>Account Number</TableCell>
                        <TableCell sx={{ width: '25%', border: '1px solid', borderColor: 'grey.300' }}>{personnel.accountNumber}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>PFA Name</TableCell>
                        <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>{personnel.pfaName}</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>PENCOM ID</TableCell>
                        <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>{personnel.pencomId}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>NHF Number</TableCell>
                        <TableCell colSpan={3} sx={{ border: '1px solid', borderColor: 'grey.300' }}>{personnel.nhfNumber || 'N/A'}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Grid>
              </Grid>

              <Divider sx={{ my: 4 }} />

              {/* Contact Information */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>Contact Information</Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Phone Number (11 digits)"
                      value={phoneNumber}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      placeholder="e.g., 08012345678"
                      error={!!phoneError}
                      helperText={phoneError || `${phoneNumber.length}/11 digits`}
                      inputProps={{ maxLength: 11, inputMode: 'numeric', pattern: '[0-9]*' }}
                      sx={{ '& .MuiInputBase-input': { fontSize: '1.1rem', padding: '16px' } }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Email Address"
                      value={email}
                      onChange={(e) => handleEmailChange(e.target.value)}
                      placeholder="e.g., john.doe@example.com" type="email"
                      error={!!emailError}
                      helperText={emailError || 'Enter a valid email address'}
                      sx={{ '& .MuiInputBase-input': { fontSize: '1.1rem', padding: '16px' } }} />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ mb: 4 }} />

              {/* ── Salary Complaints (multiple) ── */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>Salary Complaints</Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasComplaint}
                      onChange={(e) => {
                        setHasComplaint(e.target.checked);
                        if (!e.target.checked) setComplaints([{ type: '', details: '' }]);
                      }}
                    />
                  }
                  label={
                    <Typography variant="body1" fontWeight={hasComplaint ? 600 : 400}>
                      Personnel has a salary complaint
                    </Typography>
                  }
                />

                {hasComplaint && (
                  <Box sx={{ mt: 3 }}>
                    {complaints.map((complaint, index) => (
                      <Paper
                        key={index}
                        variant="outlined"
                        sx={{ p: 2.5, mb: 2, borderColor: 'primary.light', position: 'relative' }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                            Complaint {index + 1}
                          </Typography>
                          {complaints.length > 1 && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveComplaint(index)}
                              title="Remove complaint"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            Complaint Type <span style={{ color: 'red' }}>*</span>
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {complaintTypes.map((option) => (
                              <Chip
                                key={option.value}
                                label={option.label}
                                onClick={() => handleComplaintChange(index, 'type', option.value)}
                                color={complaint.type === option.value ? 'primary' : 'default'}
                                variant={complaint.type === option.value ? 'filled' : 'outlined'}
                                size="small"
                                sx={{
                                  cursor: 'pointer',
                                  fontWeight: complaint.type === option.value ? 700 : 400,
                                  borderWidth: 1.5,
                                }}
                              />
                            ))}
                          </Box>
                          {!complaint.type && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                              Please select a complaint type
                            </Typography>
                          )}
                        </Box>
                        <TextField
                          fullWidth required
                          label="Complaint Details"
                          value={complaint.details}
                          onChange={(e) => handleComplaintChange(index, 'details', e.target.value)}
                          multiline
                          rows={3}
                          placeholder="Describe this complaint in detail..."
                        />
                      </Paper>
                    ))}

                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddComplaint}
                      size="small"
                      sx={{ mt: 1 }}
                    >
                      Add Another Complaint
                    </Button>
                  </Box>
                )}
              </Box>

              <Divider sx={{ mb: 4 }} />

              {/* Non-Verification Section */}
              {!nonVerifSuccess && (
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Mark as Non-Verified</Typography>
                    <Button
                      variant={showNonVerification ? 'contained' : 'outlined'}
                      color="warning"
                      size="small"
                      onClick={() => {
                        setShowNonVerification(!showNonVerification);
                        setNonVerifReason(''); setNonVerifDetails('');
                        setNonVerifError('');
                      }}
                    >
                      {showNonVerification ? 'Cancel' : 'Personnel Cannot Verify Today'}
                    </Button>
                  </Box>

                  {showNonVerification && (
                    <Paper variant="outlined" sx={{ p: 3, borderColor: 'warning.main' }}>
                      <Alert severity="warning" sx={{ mb: 3 }}>
                        ⚠️ Use this only when the personnel is present but cannot complete verification.
                        This will be recorded against their attendance.
                      </Alert>

                      <TextField
                        select fullWidth required
                        label="Reason for Non-Verification"
                        value={nonVerifReason}
                        onChange={(e) => setNonVerifReason(e.target.value)}
                        sx={{ mb: 3 }}
                      >
                        <MenuItem value="ON_MEDICAL">On Medical</MenuItem>
                        <MenuItem value="ON_ATTACHMENT">On Attachment</MenuItem>
                        <MenuItem value="DECEASED">Deceased</MenuItem>
                        <MenuItem value="AWOL">AWOL (Absent Without Leave)</MenuItem>
                        <MenuItem value="SUSPENDED">Suspended</MenuItem>
                        <MenuItem value="DISMISSED">Dismissed</MenuItem>
                      </TextField>

                      <TextField
                        fullWidth
                        label="Additional Details (optional)"
                        value={nonVerifDetails}
                        onChange={(e) => setNonVerifDetails(e.target.value)}
                        multiline rows={3}
                        placeholder="Provide any additional details about the non-verification..."
                        sx={{ mb: 3 }}
                      />

                      {nonVerifError && (
                        <Alert severity="error" sx={{ mb: 2 }}>{nonVerifError}</Alert>
                      )}

                      <Button
                        variant="contained" color="warning" fullWidth
                        onClick={handleNonVerification}
                        disabled={nonVerifLoading}
                        startIcon={nonVerifLoading ? <CircularProgress size={20} color="inherit" /> : null}
                        sx={{ py: 1.5, fontWeight: 700 }}
                      >
                        {nonVerifLoading ? 'Recording...' : 'RECORD NON-VERIFICATION'}
                      </Button>
                    </Paper>
                  )}
                </Box>
              )}

              {/* Non-verification success — replaces the verify button entirely */}
              {nonVerifSuccess ? (
                <Box>
                  <Alert severity="success" sx={{ mb: 3 }}>
                    <Typography variant="body1" fontWeight={600} gutterBottom>
                      ✅ Non-verification recorded successfully
                    </Typography>
                    <Typography variant="body2">
                      This personnel's attendance has been marked accordingly. No further action is required for this record.
                    </Typography>
                  </Alert>
                  <Button variant="outlined" size="large" fullWidth onClick={handleReset}
                    sx={{ py: 2, fontSize: '1rem', fontWeight: 600 }}>
                    Verify Another Personnel
                  </Button>
                </Box>
              ) : (
                <>
                  {/* Error Alert */}
                  {verificationError && (
                    <Alert severity="error" sx={{ mb: 3 }}>{verificationError}</Alert>
                  )}

                  {/* Verify Button */}
                  <Button variant="contained" color="success" size="large" fullWidth
                    onClick={handleCompleteVerification} disabled={verifying}
                    startIcon={verifying ? <CircularProgress size={20} color="inherit" /> : <VerifiedIcon />}
                    sx={{ py: 2.5, fontSize: '1.1rem', fontWeight: 700 }}>
                    {verifying ? 'Verifying Personnel...' : 'VERIFY PERSONNEL'}
                  </Button>
                </>
              )}
            </>
          )}
        </Paper>
      )}

      {/* Pre-clearance Dialog — officers only */}
      {personnel && !isAdmin && (
        <PreClearanceDialog
          open={preClearanceDialogOpen}
          personnel={personnel}
          onClose={() => setPreClearanceDialogOpen(false)}
          onSuccess={handlePreClearanceSuccess}
        />
      )}
    </Container>
  );
};

export default VerificationPage;